import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function CookiePolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <div className="sticky top-0 z-50 bg-white border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img
                src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/473109dd943942010dc73e384d113dfa.png"
                alt="AK Energy"
                className="h-7 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 md:px-6 lg:px-10 py-10 md:py-14">
        <p className="text-xs text-foreground-400 uppercase tracking-wider mb-2">Last updated: July 2026</p>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">Cookie Policy</h1>

        <div className="prose prose-sm max-w-none text-foreground-700 space-y-6">
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">1. What Are Cookies?</h2>
            <p>
              Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently, improve user experience, and provide information to the website owners. Cookies may be set by us (&ldquo;first-party cookies&rdquo;) or by third-party services we use (&ldquo;third-party cookies&rdquo;).
            </p>
            <p>
              This policy explains how AK Energy (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) uses cookies and similar tracking technologies on our website. By using our website, you consent to the use of cookies in accordance with this policy.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">2. Types of Cookies We Use</h2>

            <h3 className="font-heading text-base font-semibold text-foreground-900 mt-5 mb-2">2.1 Essential Cookies</h3>
            <p>
              These cookies are necessary for the website to function properly. They enable core features such as security, network management, and accessibility. Without these cookies, parts of the website would not work. You cannot opt out of essential cookies.
            </p>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-sm border border-background-200 rounded-lg overflow-hidden">
                <thead className="bg-background-100">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Cookie</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Purpose</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  <tr>
                    <td className="px-3 py-2 text-xs text-foreground-700 font-medium">session_id</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Maintains your session state as you navigate the website</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Session</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-xs text-foreground-700 font-medium">csrf_token</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Protects against cross-site request forgery attacks</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Session</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-xs text-foreground-700 font-medium">cookie_consent</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Stores your cookie preference settings</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">12 months</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="font-heading text-base font-semibold text-foreground-900 mt-6 mb-2">2.2 Performance and Analytics Cookies</h3>
            <p>
              These cookies help us understand how visitors interact with the website by collecting and reporting information anonymously. They help us measure traffic, identify popular pages, and improve our content and user experience.
            </p>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-sm border border-background-200 rounded-lg overflow-hidden">
                <thead className="bg-background-100">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Cookie</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Purpose</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  <tr>
                    <td className="px-3 py-2 text-xs text-foreground-700 font-medium">_ga, _ga_*</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Google Analytics — tracks page views, session duration, and user behaviour</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">2 years</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-xs text-foreground-700 font-medium">_gid</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Google Analytics — distinguishes unique users</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">24 hours</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-xs text-foreground-700 font-medium">_gat</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Google Analytics — throttles request rate</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">1 minute</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="font-heading text-base font-semibold text-foreground-900 mt-6 mb-2">2.3 Functional Cookies</h3>
            <p>
              These cookies enable enhanced functionality and personalisation, such as remembering your preferences, form inputs, and display settings. They may be set by us or by third-party providers whose services we have added to our pages.
            </p>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-sm border border-background-200 rounded-lg overflow-hidden">
                <thead className="bg-background-100">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Cookie</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Purpose</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  <tr>
                    <td className="px-3 py-2 text-xs text-foreground-700 font-medium">user_prefs</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Remembers your display and language preferences</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">12 months</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-xs text-foreground-700 font-medium">recent_searches</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Stores your recent marketplace searches for convenience</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">30 days</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="font-heading text-base font-semibold text-foreground-900 mt-6 mb-2">2.4 Marketing Cookies</h3>
            <p>
              These cookies are used to deliver advertisements more relevant to you and your interests. They may be set through our website by our advertising partners to build a profile of your interests and show you relevant ads on other websites.
            </p>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-sm border border-background-200 rounded-lg overflow-hidden">
                <thead className="bg-background-100">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Cookie</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Purpose</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-foreground-600">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  <tr>
                    <td className="px-3 py-2 text-xs text-foreground-700 font-medium">_fbp</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Facebook — delivers and measures advertising campaigns</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">3 months</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-xs text-foreground-700 font-medium">_gcl_au</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">Google Ads — tracks ad conversions and campaign performance</td>
                    <td className="px-3 py-2 text-xs text-foreground-600">3 months</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">3. Third-Party Cookies</h2>
            <p>
              Some cookies on our website are set by trusted third-party services. These third parties may also collect data about your browsing activity across different websites. We work with the following third-party services:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Google Analytics</strong> — For website traffic analysis and user behaviour insights. Read Google&rsquo;s privacy policy at <a href="https://policies.google.com/privacy" target="_blank" rel="nofollow noopener" className="text-primary-600 hover:text-primary-700 underline">policies.google.com/privacy</a>.</li>
              <li><strong>Google Ads</strong> — For advertising campaign measurement and audience targeting.</li>
              <li><strong>Facebook (Meta)</strong> — For advertising campaign measurement and retargeting.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">4. How to Manage Cookies</h2>
            <p>
              Most web browsers allow you to control cookies through their settings. You can usually find these settings in the &ldquo;Options,&rdquo; &ldquo;Preferences,&rdquo; or &ldquo;Privacy&rdquo; menu of your browser. Below are links to cookie management instructions for common browsers:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Google Chrome:</strong> Settings &gt; Privacy and Security &gt; Cookies and other site data</li>
              <li><strong>Mozilla Firefox:</strong> Options &gt; Privacy &amp; Security &gt; Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies and website data</li>
              <li><strong>Microsoft Edge:</strong> Settings &gt; Site permissions &gt; Cookies and site data</li>
            </ul>
            <p className="mt-2">
              Please note that disabling certain cookies may affect the functionality of our website, and some features may not work as intended. Essential cookies cannot be disabled as they are required for the website to function.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">5. Changes to This Cookie Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in the cookies we use, our practices, or for operational, legal, or regulatory reasons. We will post any changes on this page and update the &ldquo;Last updated&rdquo; date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">6. Related Policies</h2>
            <p>
              This Cookie Policy should be read in conjunction with our <Link to="/privacy-policy" className="text-primary-600 hover:text-primary-700 underline">Privacy Policy</Link> and <Link to="/terms-of-service" className="text-primary-600 hover:text-primary-700 underline">Terms of Service</Link>, which provide further information about how we handle your personal data.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">7. Contact Us</h2>
            <p>
              If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <div className="mt-3 space-y-1 text-sm">
              <p><strong>Email:</strong> akenergyau@gmail.com</p>
              <p><strong>Phone:</strong> 1800 253 637</p>
              <p><strong>Post:</strong> AK Energy, GPO Box 1234, Sydney NSW 2001, Australia</p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}